import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  Box,
  Button,
  Link,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMe, updateMe } from "../api/users";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import { useConfig } from "../contexts/ConfigContext";

const MAIN_DRAWER_WIDTH_PX = 280;

function AccountSettings() {
  const navigate = useNavigate();
  const config = useConfig();
  const checkListEnabled = Boolean(config?.features?.enableCheckList);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [checklistNotificationEmail, setChecklistNotificationEmail] =
    useState("");
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const me = await getMe();
        if (!cancelled) {
          setUsername(me.username || "");
          setDisplayName(me.display_name || "");
          setEmail(me.email || "");
          setChecklistNotificationEmail(
            me.checklist_notification_email || ""
          );
        }
      } catch (error) {
        if (!cancelled) {
          setFeedback({
            open: true,
            message: "ユーザー情報の取得に失敗しました。",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        display_name: displayName,
        email: email || null,
      };
      if (checkListEnabled) {
        payload.checklist_notification_email =
          checklistNotificationEmail || null;
      }
      const updated = await updateMe(payload);
      setDisplayName(updated.display_name || "");
      setEmail(updated.email || "");
      setChecklistNotificationEmail(
        updated.checklist_notification_email || ""
      );
      window.dispatchEvent(new Event("user-profile-updated"));
      setFeedback({
        open: true,
        message: "アカウント情報を保存しました。",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error.message || "アカウント情報の保存に失敗しました。",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Stack spacing={2} sx={{ pb: 12, maxWidth: 600 }}>
        <Paper sx={{ p: 2.5 }}>
          <Stack spacing={2.5}>
            <TextField
              label="ログインID"
              value={username}
              InputProps={{ readOnly: true }}
              helperText="ログインIDは変更できません"
            />

            <Box>
              <TextField
                label="パスワード"
                value="••••••••"
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate("/account/change-password")}
                sx={{ mt: 0.5 }}
              >
                パスワードを変更する
              </Link>
            </Box>

            <TextField
              label="表示名"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />

            <TextField
              label="ワースケ通知メールアドレス"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {checkListEnabled && (
              <TextField
                label="チェックリスト通知メールアドレス"
                type="email"
                value={checklistNotificationEmail}
                onChange={(e) =>
                  setChecklistNotificationEmail(e.target.value)
                }
              />
            )}
          </Stack>
        </Paper>
      </Stack>

      <Paper
        component="footer"
        elevation={0}
        square
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: { xs: 0, lg: `${MAIN_DRAWER_WIDTH_PX}px` },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: { xs: 2, sm: 3, lg: 4 },
          py: 1.5,
          pb: {
            xs: "calc(12px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(12px + env(safe-area-inset-bottom, 0px))",
          },
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(14px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            maxWidth: { lg: 1200 },
            mx: { lg: "auto" },
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 180 },
            }}
          >
            {saving ? "保存中..." : "設定を保存する"}
          </Button>
        </Box>
      </Paper>

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

export default AccountSettings;
